"use client";

import { SiteTemplatePageSectionTypeCommoditiesSettingModel } from "@/Apis/ClinicApi";
import {
  FavoriteToggle,
  Format,
  If,
  SmartImage,
  SmartLink,
} from "@/Components";
import { Helper } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useImageFallback, useT } from "@/Hooks";
import { CommoditiesSectionProps as _CommoditiesSectionProps } from "@/Templates/SectionsProps";
import _ from "lodash";
import { FC, useMemo } from "react";
import { RiArrowLeftSLine } from "react-icons/ri";
import HomeThirteenProductSectionGenerated, {
  type HomeThirteenProductSectionGeneratedProps,
} from "./_generated/HomeThirteenProductSection.generated";

interface CommoditiesSectionProps extends _CommoditiesSectionProps {
  showBackgroundImage?: boolean;
  showViewAllButton?: boolean;
  syncDepartmentStatsWithItems?: boolean;
}

const TAB_PANE_IDS = [
  "all",
  "pain",
  "antibiotic",
  "cold",
  "diabetes",
  "heart",
  "nutrition",
  "devices",
] as const;

type TabPaneId = (typeof TAB_PANE_IDS)[number];
type CommodityDepartmentEntry =
  _CommoditiesSectionProps["dataSource"]["Departments"][number] & {
    tabId: TabPaneId;
    tabKey: string;
    tabContentClassName: string;
    items: _CommoditiesSectionProps["dataSource"]["Items"];
  };
type CommodityItem = _CommoditiesSectionProps["dataSource"]["Items"][number];
type ProductSectionTabPaneItem =
  HomeThirteenProductSectionGeneratedProps["productSectionTabPaneFadeShowActiveFadeInUps"][number];

const Commodities: FC<CommoditiesSectionProps> = ({
  setting,
  dataSource,
  pageData,
  showBackgroundImage: _showBackgroundImage = true,
  showViewAllButton: _showViewAllButton = true,
  syncDepartmentStatsWithItems = false,
}) => {
  const t = useT();
  const items = dataSource.Items;

  const departments = useMemo(() => {
    if (!syncDepartmentStatsWithItems) {
      return dataSource.Departments;
    }

    return dataSource.Departments.map((department) => ({
      ...department,
      HasMore: false,
      ItemsCount: items.filter(
        (item) => item.CenterDepartmentId === department.Id,
      ).length,
    }));
  }, [dataSource.Departments, items, syncDepartmentStatsWithItems]);

  const visibleDepartments = useMemo(
    () => departments.slice(0, TAB_PANE_IDS.length),
    [departments],
  );

  const departmentEntries = useMemo<CommodityDepartmentEntry[]>(
    () =>
      visibleDepartments.map((department, index) => ({
        ...department,
        tabId: TAB_PANE_IDS[index],
        tabKey: `${TAB_PANE_IDS[index]}-tab`,
        tabContentClassName:
          index === 0 ? "row g-4 justify-content-center" : "row g-4",
        items: items.filter(
          (item) => item.CenterDepartmentId === department.Id,
        ),
      })),
    [items, visibleDepartments],
  );

  const departmentEntryByTabId = useMemo(
    () =>
      new Map<string, CommodityDepartmentEntry>(
        departmentEntries.map((entry) => [entry.tabId, entry]),
      ),
    [departmentEntries],
  );

  const getDisplayName = (item: CommodityItem) =>
    item.DisplayNameInProfile || item.Name;

  const buildCommodityCardBase = (item: CommodityItem | undefined) => ({
    src: item?.Image?.Url ?? "",
    alt: item?.Image?.AltText ?? item?.Name ?? "",
    link0Text: item ? getDisplayName(item) : "",
    href: item?.Link ?? "#",
    link0Href: item?.Link ?? "#",
    p0Text: item?.ShortDescription ?? "",
    h40Text: item ? (
      <Format
        type="Number"
        value={item.SpecialSalePrice ?? item.SalePrice}
        currency={pageData.CommonData.Currency}
      />
    ) : (
      ""
    ),
    span0Text: "",
    span0Span0Text: "",
  });

  const productSectionTabPaneFadeShowActiveFadeInUps = useMemo<
    ProductSectionTabPaneItem[]
  >(
    () =>
      departmentEntries.map((entry, index) => ({
        key: `${entry.tabId}-${entry.Id}`,
        id: entry.tabId,
        ariaLabelledby: entry.tabKey,
        className:
          index === 0
            ? "tab-pane fade show active wow fadeInUp"
            : "tab-pane fade",
        dataWowDuration: index === 0 ? "1s" : undefined,
        div0ClassName:
          index === 0 ? "row g-4 justify-content-center" : "row g-4",
        ...buildCommodityCardBase(entry.items[0] ?? items[0]),
      })),
    [
      buildCommodityCardBase,
      departmentEntries,
      items,
      pageData.CommonData.Currency,
    ],
  );

  const productSectionNavs = useMemo(
    () =>
      departmentEntries.map((entry) => ({
        key: entry.tabKey,
        button0Text: entry.Name,
        ariaControls: entry.tabId,
        ariaSelected: entry.tabId === "all",
        className: entry.tabId === "all" ? "nav-link active" : "nav-link",
        dataBsTarget: `#${entry.tabId}`,
        id: entry.tabKey,
        dataBsToggle: "tab",
        liClassName: "nav-item",
      })),
    [departmentEntries],
  );

  const getImageFallback = useImageFallback();

  const renderGeneratedCommodityCard = (
    item: CommodityItem,
    currency?: number,
  ) => {
    const displayName = item.DisplayNameInProfile || item.Name;
    const price = item.SpecialSalePrice ?? item.SalePrice;

    return (
      <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6" key={item.Id}>
        <div className="deals-item-thirteen">
          <div className="delas-overlay">
            <SmartImage
              src={getImageFallback({
                imageUrl: item.Image?.Url,
                entity: "CenterItem",
              })}
              alt={item.Image?.AltText ?? displayName}
              className="img-fluid img-1"
            />
            <div className="delas-badge justify-content-end">
              <FavoriteToggle
                ids={{
                  type: CenterOptions.Enum.UserFavoriteType.CenterItem.key,
                  centerItemId: item.Id,
                }}
                defaultFavorite={item.Favorite}
                variant="button"
                shape="circle"
              />
            </div>
          </div>
          <div className="deals-content">
            <div className="title">
              <h3 className="custom-title">
                <SmartLink href={item.Link}>{displayName}</SmartLink>
              </h3>
              <p
                className="description"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.ShortDescription}
              </p>
            </div>
            <div className="price-item">
              <h4 className="count-price">
                {price !== undefined ? (
                  <Format type="Number" value={price} currency={currency} />
                ) : null}
              </h4>
            </div>
          </div>
          {/* <div className="deals-footer">
                        <SmartLink href={item.Link} className="btn theme-12-btn">
                            <i className="isax isax-shopping-cart ms-2" />
                            {displayName}
                        </SmartLink>
                    </div> */}
        </div>
      </div>
    );
  };

  if (_.isEmpty(items)) return <></>;

  return (
    <HomeThirteenProductSectionGenerated
      sectionDiv0Div0H20H2Text={setting.Title}
      sectionDiv0Div0Link0LinkVisible={false}
      productSectionNavs={productSectionNavs}
      productSectionTabPaneFadeShowActiveFadeInUps={
        productSectionTabPaneFadeShowActiveFadeInUps
      }
      productSectionTabPaneFadeShowActiveFadeInUpsItemWrapper={(item) => {
        const entry = departmentEntryByTabId.get(item.id);
        if (!entry) {
          return null;
        }

        return (
          <div
            className={item.className}
            data-wow-duration={item.dataWowDuration}
            id={item.id}
            role="tabpanel"
            aria-labelledby={item.ariaLabelledby}
            key={item.key}
          >
            <div className={entry.tabContentClassName}>
              {entry.items.map((commodity) =>
                renderGeneratedCommodityCard(
                  commodity,
                  pageData.CommonData.Currency,
                ),
              )}
            </div>
            <If condition={_showViewAllButton && entry.HasMore}>
              <div className="text-center load-item aos" data-aos="fade-up">
                <SmartLink href={entry.Link} className="btn btn-dark">
                  {t(
                    "landing.commodities.viewAllCategoryItems",
                    undefined,
                    "View all items in this category",
                  )}
                  <RiArrowLeftSLine className="ms-2" />
                </SmartLink>
              </div>
            </If>
          </div>
        );
      }}
    />
  );
};

export default Commodities;

export const defaultCommoditiesSetting: SiteTemplatePageSectionTypeCommoditiesSettingModel =
{
  Title: "",
  Description: "",
  Type: Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.CenterDepartmentType.Service),
  ItemsDisplayMode: Helper.centerOptionObjectToCenterOptionModel(
    CenterOptions.SiteTemplate.SiteTemplateSettings.ItemsDisplayMode.Grid,
  ),
  DisplaySubordinateCenterData: Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.Status.InActive),
  ShowServicesCommoditiesWithCategory: Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.Status.InActive),
};
